import type { PlrAssessment13849 } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { cn } from '@/shared/lib/utils'

type PlrSistemaKey = 'plA' | 'plB' | 'plC' | 'plD' | 'plE'

const PLR_FILL: Record<PlrSistemaKey, string> = {
  plA: '#10b981',
  plB: '#84cc16',
  plC: '#eab308',
  plD: '#f97316',
  plE: '#ef4444',
}

const PLR_TEXT: Record<PlrSistemaKey, string> = {
  plA: '#000000',
  plB: '#000000',
  plC: '#000000',
  plD: '#000000',
  plE: '#000000',
}

type GraphNode = {
  id: string
  label: string
  centerX: number
  centerY: number
  plrKey?: PlrSistemaKey
}

type GraphPath = {
  key: string
  severity: 'S1' | 'S2'
  frequency: 'F1' | 'F2'
  possibility: 'P1' | 'P2'
  plrGrade: 'A' | 'B' | 'C' | 'D' | 'E'
  nodeIds: [string, string, string, string]
}

const GRAPH_PATHS: GraphPath[] = [
  {
    key: 'S1F1P1',
    severity: 'S1',
    frequency: 'F1',
    possibility: 'P1',
    plrGrade: 'A',
    nodeIds: ['s1', 's1f1', 's1f1p1', 'pla'],
  },
  {
    key: 'S1F1P2',
    severity: 'S1',
    frequency: 'F1',
    possibility: 'P2',
    plrGrade: 'B',
    nodeIds: ['s1', 's1f1', 's1f1p2', 'plb1'],
  },
  {
    key: 'S1F2P1',
    severity: 'S1',
    frequency: 'F2',
    possibility: 'P1',
    plrGrade: 'B',
    nodeIds: ['s1', 's1f2', 's1f2p1', 'plb2'],
  },
  {
    key: 'S1F2P2',
    severity: 'S1',
    frequency: 'F2',
    possibility: 'P2',
    plrGrade: 'C',
    nodeIds: ['s1', 's1f2', 's1f2p2', 'plc1'],
  },
  {
    key: 'S2F1P1',
    severity: 'S2',
    frequency: 'F1',
    possibility: 'P1',
    plrGrade: 'C',
    nodeIds: ['s2', 's2f1', 's2f1p1', 'plc2'],
  },
  {
    key: 'S2F1P2',
    severity: 'S2',
    frequency: 'F1',
    possibility: 'P2',
    plrGrade: 'D',
    nodeIds: ['s2', 's2f1', 's2f1p2', 'pld1'],
  },
  {
    key: 'S2F2P1',
    severity: 'S2',
    frequency: 'F2',
    possibility: 'P1',
    plrGrade: 'D',
    nodeIds: ['s2', 's2f2', 's2f2p1', 'pld2'],
  },
  {
    key: 'S2F2P2',
    severity: 'S2',
    frequency: 'F2',
    possibility: 'P2',
    plrGrade: 'E',
    nodeIds: ['s2', 's2f2', 's2f2p2', 'ple'],
  },
]

const GRAPH_NODES: GraphNode[] = [
  { id: 's1', label: 'S1', centerX: 28, centerY: 64 },
  { id: 's2', label: 'S2', centerX: 28, centerY: 192 },
  { id: 's1f1', label: 'F1', centerX: 96, centerY: 32 },
  { id: 's1f2', label: 'F2', centerX: 96, centerY: 96 },
  { id: 's2f1', label: 'F1', centerX: 96, centerY: 160 },
  { id: 's2f2', label: 'F2', centerX: 96, centerY: 224 },
  { id: 's1f1p1', label: 'P1', centerX: 164, centerY: 16 },
  { id: 's1f1p2', label: 'P2', centerX: 164, centerY: 48 },
  { id: 's1f2p1', label: 'P1', centerX: 164, centerY: 80 },
  { id: 's1f2p2', label: 'P2', centerX: 164, centerY: 112 },
  { id: 's2f1p1', label: 'P1', centerX: 164, centerY: 144 },
  { id: 's2f1p2', label: 'P2', centerX: 164, centerY: 176 },
  { id: 's2f2p1', label: 'P1', centerX: 164, centerY: 208 },
  { id: 's2f2p2', label: 'P2', centerX: 164, centerY: 240 },
  { id: 'pla', label: 'a', centerX: 232, centerY: 16, plrKey: 'plA' },
  { id: 'plb1', label: 'b', centerX: 232, centerY: 48, plrKey: 'plB' },
  { id: 'plb2', label: 'b', centerX: 232, centerY: 80, plrKey: 'plB' },
  { id: 'plc1', label: 'c', centerX: 232, centerY: 112, plrKey: 'plC' },
  { id: 'plc2', label: 'c', centerX: 232, centerY: 144, plrKey: 'plC' },
  { id: 'pld1', label: 'd', centerX: 232, centerY: 176, plrKey: 'plD' },
  { id: 'pld2', label: 'd', centerX: 232, centerY: 208, plrKey: 'plD' },
  { id: 'ple', label: 'e', centerX: 232, centerY: 240, plrKey: 'plE' },
]

const GRAPH_EDGES: Array<{ from: string; to: string }> = Array.from(
  new Map(
    GRAPH_PATHS.flatMap((path) => {
      const [sNode, fNode, pNode, plrNode] = path.nodeIds
      return [
        { from: sNode, to: fNode },
        { from: fNode, to: pNode },
        { from: pNode, to: plrNode },
      ]
    }).map((edge) => [`${edge.from}->${edge.to}`, edge] as const)
  ).values()
)

const NODE_WIDTH = 36
const NODE_HEIGHT = 22
const HEADER_BASELINE_Y = 12
const GRAPH_TOP_OFFSET = 26

const GRAPH_NODES_OFFSET: GraphNode[] = GRAPH_NODES.map((node) => ({
  ...node,
  centerY: node.centerY + GRAPH_TOP_OFFSET,
}))

const NODE_BY_ID_OFFSET = new Map(
  GRAPH_NODES_OFFSET.map((node) => [node.id, node])
)

function getAssessmentPathKey(assessment: PlrAssessment13849): string {
  return `${assessment.severity}${assessment.frequency}${assessment.possibilityOfAvoidance}`
}

function getActivePath(assessment: PlrAssessment13849): GraphPath | null {
  const pathKey = getAssessmentPathKey(assessment)
  return GRAPH_PATHS.find((path) => path.key === pathKey) ?? null
}

function getEdgeMidpoints(
  from: GraphNode,
  to: GraphNode
): { x1: number; y1: number; x2: number; y2: number } {
  return {
    x1: from.centerX + NODE_WIDTH / 2,
    y1: from.centerY,
    x2: to.centerX - NODE_WIDTH / 2,
    y2: to.centerY,
  }
}

type EnIso13849RiskGraphProps = {
  assessment: PlrAssessment13849
  ariaLabel: string
  className?: string
}

export function EnIso13849RiskGraph({
  assessment,
  ariaLabel,
  className,
}: EnIso13849RiskGraphProps) {
  const activePath = getActivePath(assessment)
  const activeNodeIds = new Set(activePath?.nodeIds ?? [])
  const activeEdgeKeys = new Set<string>()

  if (activePath != null) {
    const [sNode, fNode, pNode, plrNode] = activePath.nodeIds
    activeEdgeKeys.add(`${sNode}->${fNode}`)
    activeEdgeKeys.add(`${fNode}->${pNode}`)
    activeEdgeKeys.add(`${pNode}->${plrNode}`)
  }

  return (
    <svg
      viewBox={`0 0 280 ${268 + GRAPH_TOP_OFFSET}`}
      role='img'
      aria-label={ariaLabel}
      className={cn('h-auto w-full max-w-md text-foreground', className)}
    >
      <text
        x={28}
        y={HEADER_BASELINE_Y}
        textAnchor='middle'
        className='fill-muted-foreground text-[10px] font-medium'
      >
        S
      </text>
      <text
        x={96}
        y={HEADER_BASELINE_Y}
        textAnchor='middle'
        className='fill-muted-foreground text-[10px] font-medium'
      >
        F
      </text>
      <text
        x={164}
        y={HEADER_BASELINE_Y}
        textAnchor='middle'
        className='fill-muted-foreground text-[10px] font-medium'
      >
        P
      </text>
      <text
        x={232}
        y={HEADER_BASELINE_Y}
        textAnchor='middle'
        className='fill-muted-foreground text-[10px] font-medium'
      >
        PLr
      </text>

      {GRAPH_EDGES.map((edge) => {
        const fromNode = NODE_BY_ID_OFFSET.get(edge.from)
        const toNode = NODE_BY_ID_OFFSET.get(edge.to)
        if (fromNode == null || toNode == null) {
          return null
        }

        const edgeKey = `${edge.from}->${edge.to}`
        const isActive = activeEdgeKeys.has(edgeKey)
        const { x1, y1, x2, y2 } = getEdgeMidpoints(fromNode, toNode)

        return (
          <line
            key={edgeKey}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            className={cn(
              isActive ? 'stroke-foreground' : 'stroke-muted-foreground/35'
            )}
            strokeWidth={isActive ? 2 : 1}
          />
        )
      })}

      {GRAPH_NODES_OFFSET.map((node) => {
        const isActive = activeNodeIds.has(node.id)
        const isPlrNode = node.plrKey != null
        let fill = 'var(--background)'
        let textFill = 'currentColor'
        let strokeClass = isActive
          ? 'stroke-foreground'
          : 'stroke-muted-foreground/50'
        let strokeColor: string | undefined

        if (isPlrNode && node.plrKey != null) {
          if (isActive) {
            fill = PLR_FILL[node.plrKey]
            textFill = PLR_TEXT[node.plrKey]
          } else {
            strokeColor = PLR_FILL[node.plrKey]
            strokeClass = ''
          }
        } else if (isActive) {
          fill = 'var(--muted)'
        }

        const rectX = node.centerX - NODE_WIDTH / 2
        const rectY = node.centerY - NODE_HEIGHT / 2

        return (
          <g key={node.id}>
            <rect
              x={rectX}
              y={rectY}
              width={NODE_WIDTH}
              height={NODE_HEIGHT}
              rx={4}
              fill={fill}
              stroke={strokeColor}
              strokeOpacity={strokeColor != null ? 0.5 : undefined}
              className={strokeClass}
              strokeWidth={isActive ? 2 : 1}
            />
            <text
              x={node.centerX}
              y={node.centerY + 4}
              textAnchor='middle'
              className='text-[11px] font-semibold'
              fill={textFill}
            >
              {node.label}
            </text>
          </g>
        )
      })}
    </svg>
  )
}
